import i18next, { type i18n as I18nInstance } from "i18next"
import { initReactI18next } from "react-i18next"

import { bindDocumentLang } from "@/lib/i18n/document-lang"
import type { Locale } from "@/types"

/**
 * i18next client bootstrap for Inertia.
 *
 * Locale resolution:
 *   - Primary: root-level `locale` shared prop (`initialPage.props.locale`),
 *     set by Backend via `Current.user&.locale || I18n.locale.to_s`. Consumers
 *     read this in the Inertia `setup` callback and call `i18n.changeLanguage`
 *     before render, so the very first paint is already localised.
 *   - Fallback: `"fr"` (project default per CLAUDE.md) — protects anonymous
 *     pages in the rare case the root prop is ever missing.
 *
 * Translation bundles:
 *   - Generated by the `inertia_i18n` Rails gem from `config/locales/frontend/**`
 *     into `app/frontend/locales/<locale>.json`. The gem MERGES every
 *     per-namespace YAML file into a single tree keyed by top-level YAML key
 *     (e.g. `creator_home.*`, `nav.*`, `roles.*`). This means i18next runs with
 *     a SINGLE namespace (the default `"translation"`) and call sites resolve
 *     via the full key path (`t("creator_home.title")`, `t("nav.dashboard")`).
 *   - The gem rewrites Rails `%{var}` interpolation tokens into i18next's
 *     default `{{var}}` at build time, so the frontend runs with default
 *     i18next interpolation syntax. YAML stays Rails-valid; JSON is
 *     i18next-native.
 *   - Bundled via Vite's static glob — NO runtime HTTP fetch, no extra
 *     failure mode, and SSR works out of the box without a fetch polyfill.
 */

export const DEFAULT_LOCALE: Locale = "fr"
export const AVAILABLE_LOCALES: readonly Locale[] = ["en", "fr"] as const

type TranslationBundle = Record<string, unknown>

/**
 * Eager-load every generated locale JSON. File layout matches the
 * `inertia_i18n` gem's default: `app/frontend/locales/<locale>.json`,
 * one merged bundle per locale.
 *
 * `eager: true` inlines the modules into the chunk — no dynamic import
 * round-trip at runtime.
 */
const bundleModules = import.meta.glob<{ default: TranslationBundle }>(
  "../../locales/*.json",
  { eager: true },
)

function buildResources(
  overrides: Partial<Record<Locale, TranslationBundle>> = {},
): Record<Locale, { translation: TranslationBundle }> {
  const grouped: Record<Locale, TranslationBundle> = { en: {}, fr: {} }
  for (const [path, mod] of Object.entries(bundleModules)) {
    // path shape: "../../locales/<locale>.json"
    const match = /\/locales\/([^/]+)\.json$/.exec(path)
    if (!match) continue
    const [, rawLocale] = match
    if (!AVAILABLE_LOCALES.includes(rawLocale as Locale)) continue
    grouped[rawLocale as Locale] = mod.default
  }

  const resources: Record<Locale, { translation: TranslationBundle }> = {
    en: { translation: {} },
    fr: { translation: {} },
  }
  for (const locale of AVAILABLE_LOCALES) {
    resources[locale] = {
      translation: { ...grouped[locale], ...(overrides[locale] ?? {}) },
    }
  }
  return resources
}

export interface InitI18nOptions {
  /** Locale to start in. Falls back to `DEFAULT_LOCALE` when omitted. */
  locale?: Locale
  /**
   * Per-locale translation overrides — used by tests to inject stub bundles.
   * Production paths never pass this; the generated JSON bundles are
   * sufficient.
   */
  resources?: Partial<Record<Locale, TranslationBundle>>
  /**
   * When provided, initialises a fresh i18next instance (useful for tests
   * that want hermetic state). Omitted → mutate the shared default export.
   */
  instance?: I18nInstance
}

const BASE_CONFIG = {
  returnNull: false,
  returnEmptyString: false,
  saveMissing: false,
  // Gem outputs `{{var}}` — i18next default interpolation; no config needed.
  interpolation: {
    escapeValue: false, // React escapes output.
  },
  react: { useSuspense: false },
} as const

/**
 * Initialise i18next. Safe to call more than once on the shared instance —
 * i18next's `init` returns a resolved promise on subsequent calls.
 */
export function initI18n(options: InitI18nOptions = {}): I18nInstance {
  const target = options.instance ?? i18next
  const locale = options.locale ?? DEFAULT_LOCALE
  const resources = buildResources(options.resources)

  if (!target.isInitialized) {
    // Always register the react-i18next binding before init. Fresh
    // per-request SSR instances (passed via `options.instance`) need it so
    // `useTranslation` resolves through the `<I18nextProvider>` context —
    // mirrors `createTestI18n()`. The `isInitialized` guard means this runs
    // at most once per instance, so the shared singleton is unaffected on
    // SPA-navigation re-calls (those fall through to `changeLanguage`).
    void target.use(initReactI18next).init({
      ...BASE_CONFIG,
      resources,
      lng: locale,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...AVAILABLE_LOCALES],
      // Single merged namespace per locale — gem flattens every YAML file
      // into one tree, so we don't split on the client.
      ns: ["translation"],
      defaultNS: "translation",
    })
  } else if (target.language !== locale) {
    void target.changeLanguage(locale)
  }

  // OKS-1023 — `<html lang>` follows the shared instance's language. Only the
  // shared singleton: a caller-supplied `options.instance` is an SSR
  // per-request or hermetic test instance and must never write to `document`.
  if (!options.instance) bindDocumentLang(target)

  return target
}

/**
 * Sync the shared instance to a new locale. Called from the Inertia entrypoint
 * when the page payload exposes a different locale (e.g. user login changed
 * `currentUser.locale`).
 */
export function syncI18nLocale(locale: Locale | undefined): void {
  if (!locale) return
  if (!AVAILABLE_LOCALES.includes(locale)) return
  if (!i18next.isInitialized) {
    initI18n({ locale })
    return
  }
  if (i18next.language !== locale) {
    void i18next.changeLanguage(locale)
  }
}

export default i18next
